/**
 * Admin → User Activity: the Timeline view.
 *
 * The "time dispersion" ask: for one day, each person's work laid out across
 * the clock as blocks — so you can see they spent twenty minutes in a cluster,
 * went quiet for two hours, then came back for five minutes of touches.
 * Clicking a person unfolds the same thing as a sequence, LATEST FIRST, with
 * the idle gap called out between bursts.
 *
 * The sessions are reconstructed client-side from the per-loan audit feed —
 * `useUserActivitySessions.js` explains the join and what it costs. NOTHING
 * loads on its own: a row fetches when you open it (capped at
 * MAX_DEALS_PER_ROW loans), and the whole day only from a button that quotes
 * the request count first. Which means a strip is often a SAMPLE of the day,
 * and every place that shows one says so.
 *
 * ── This view's day is an EASTERN day ──────────────────────────────────────
 * The rest of the screen shows the backend's UTC bucket. A clock can't: UTC day
 * D runs D-1 8:00 PM → D 8:00 PM Eastern, so filtering by it drew last night's
 * 9 PM session on today's strip and produced a chip that looked like tomorrow.
 * So each chip here is a real Eastern day, assembled from the TWO UTC rows that
 * can contain it (D and D+1) and then filtered by each event's Eastern date.
 * The consequence: a row's totals only become exact once loaded — before that
 * they're an upper bound, because a UTC row straddles two Eastern days.
 */
import { useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { slate, teal } from "../../../theme/colors";
import {
  IDLE_GAP_MIN,
  formatDuration,
  formatHour,
  fieldChange,
  isSystem,
  isViewCategory,
  nextDayIso,
  num,
  relativeDayEastern,
  sessionEditMix,
  shortDay,
} from "./userActivityModel";
import { S, UserIdentity, fmtInt } from "./userActivityUi";
// The drilldown's Activity Log already knows how to name an event and render a
// changed field; these are the same audit records, so inventing a second
// vocabulary here would be the wrong kind of new code.
import {
  FIELD_LABELS,
  actionPhrase,
  detailFields,
  fmtVal,
} from "../../LoanDrilldownScreen/sidebar/types/LoanChangelog/loanChangelogConfig";
import { MAX_DEALS_PER_ROW } from "./useUserActivitySessions";

const NAME_W = 240;
const TOTALS_W = 178;

// A floor for the axis so a day with one 10-minute session doesn't get an axis
// zoomed to ten minutes, which reads as a full day of work.
const MIN_SPAN_H = 5;

// Field rows shown per event before the rest collapse into a count. A bulk save
// can carry thirty keys, and the point here is the shape of the edit.
const MAX_FIELDS = 6;

/**
 * ONE left gutter for the whole session sequence, so every line starts on the
 * same vertical. The chevron sits centred in it, the rules (dotted for an idle
 * gap, solid for an opened breakdown) run down its middle, and every line of
 * text — session header, loan header, event — begins at its right edge.
 *
 * These used to be three independent paddings that drifted to 27 / 18 / 14px.
 * Derive, don't retype: SEQ_RULE_X is the rule's left offset and the chevron's
 * box is the full gutter, so both centre on the same pixel.
 */
const SEQ_GUTTER = 26;
const SEQ_RULE_X = SEQ_GUTTER / 2 - 1;
const SEQ_RULE_W = 2;
const SEQ_TEXT_PL = SEQ_GUTTER - SEQ_RULE_X - SEQ_RULE_W;

/**
 * The axis frames the work that's actually there, padded to whole hours,
 * instead of a hardcoded 6am–8pm — a night shift or a 5am start would otherwise
 * pile up against the edge.
 */
const axisFor = (spans) => {
  if (!spans.length) return { from: 8, to: 18, ticks: [8, 10, 12, 14, 16, 18] };
  let from = Math.floor(Math.min(...spans.map((s) => s.start)));
  let to = Math.ceil(Math.max(...spans.map((s) => s.end)));
  if (to - from < MIN_SPAN_H) {
    const pad = (MIN_SPAN_H - (to - from)) / 2;
    from = Math.max(0, Math.floor(from - pad));
    to = Math.min(24, Math.ceil(to + pad));
  }
  const step = to - from > 12 ? 3 : to - from > 6 ? 2 : 1;
  const ticks = [];
  for (let h = from; h <= to; h += step) ticks.push(h);
  return { from, to, ticks };
};

const pctIn = (axis, h) =>
  Math.max(0, Math.min(100, ((h - axis.from) / (axis.to - axis.from)) * 100));

const tickLabel = (h) =>
  h === 0 || h === 24 ? "12a" : h === 12 ? "12p" : h < 12 ? `${h}a` : `${h - 12}p`;

/** A session with no edits in it was a reading session — greyed, like Views. */
const blockColor = (s) => (num(s.edits) ? teal[600] : slate[400]);

/**
 * The chip's label, EASTERN. Today and Yesterday both carry their date beside
 * them: the relative word is what you scan for, the date is what you quote when
 * you tell someone what you found.
 */
const formatDayChip = (iso) => {
  const rel = relativeDayEastern(iso);
  return rel ? { label: rel, date: shortDay(iso) } : { label: shortDay(iso) };
};

/** The edit mix behind a session's edit count — the grid's series, same colours. */
const EditMixTooltip = ({ session }) => {
  const mix = sessionEditMix(session).filter((m) => m.value > 0);
  return (
    <Box sx={{ py: 0.25, fontSize: 12, lineHeight: 1.7, whiteSpace: "nowrap" }}>
      {mix.length === 0 ? (
        <span>No edits in this session</span>
      ) : (
        mix.map((m) => (
          <Flx key={m.key} ac jb g={2.25}>
            <Flx ac g={0.75}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "2.5px",
                  bgcolor: m.color,
                  flexShrink: 0,
                }}
              />
              <span>{m.label}</span>
            </Flx>
            <b style={{ fontVariantNumeric: "tabular-nums" }}>{fmtInt(m.value)}</b>
          </Flx>
        ))
      )}
    </Box>
  );
};

EditMixTooltip.propTypes = { session: PropTypes.object };

/**
 * The loans a session touched, each opening the drilldown in a new tab.
 *
 * Rendered inline rather than behind a hover: "which loans" is the question you
 * ask right after "when", and a link you have to hover to discover isn't one
 * you'll use. A loan the pipeline couldn't identify still shows — as plain
 * un-clickable text, since there's no id to open.
 */
const LOAN_CHIP_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: "3px",
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1.6,
  px: 0.875,
  borderRadius: "6px",
  bgcolor: teal[50],
  border: `1px solid ${slate[200]}`,
  whiteSpace: "nowrap",
};

const SessionLoans = ({ session }) => {
  const loans = session.loanList || [];
  if (!loans.length) return null;
  return (
    <Flx ac g={0.75} wrap>
      {loans.map((l) => {
        const label = l.loanNumber != null ? `#${l.loanNumber}` : "loan";
        if (!l.dealId) {
          return (
            <Box
              key={label}
              component="span"
              sx={{ ...LOAN_CHIP_SX, color: S.sub }}
            >
              {label}
            </Box>
          );
        }
        return (
          <Tooltip
            key={l.dealId}
            arrow
            title={l.loanName || "Open this loan in a new tab"}
          >
            {/* A plain anchor, NOT the shared OpenInNewTabButton: that component
                spreads its `sx` onto its link icon as well as the link, so the
                chip's own border/radius/fill landed on the icon too and drew a
                second, empty pill beside every loan number. The icon belongs
                INSIDE the chip. */}
            <Box
              component="a"
              href={`${window.location.origin}/loan/${l.dealId}`}
              target="_blank"
              rel="noreferrer"
              // The chip sits inside the session row, which toggles the
              // breakdown on click — opening a loan must not also collapse the
              // thing you were reading.
              onClick={(e) => e.stopPropagation()}
              sx={{
                ...LOAN_CHIP_SX,
                color: teal[700],
                textDecoration: "none",
                transition: "background .12s, border-color .12s",
                "&:hover": { bgcolor: teal[100], borderColor: teal[300] },
              }}
            >
              {label}
              <OpenInNewRounded sx={{ fontSize: 11, opacity: 0.75 }} />
            </Box>
          </Tooltip>
        );
      })}
    </Flx>
  );
};

SessionLoans.propTypes = { session: PropTypes.object };

/**
 * What actually changed inside one session, grouped by loan.
 *
 * The edits are the point — views are counted but not enumerated, since "opened
 * a file" has no before/after to show. Within a loan the events run in the
 * order they happened, each with its field diffs.
 *
 * Before/after is available for some events and not others, and the UI says
 * which: the retroactive audit writer stores a real `{prior, updated}` diff,
 * while the live listener sink stores the submitted body — the new value only.
 * See `fieldChange`.
 */
const SessionDetail = ({ session }) => {
  const items = session.items || [];
  const byLoan = new Map();
  items.forEach((e) => {
    const key = e.dealId || e.loanNumber || "unknown";
    if (!byLoan.has(key)) {
      byLoan.set(key, {
        dealId: e.dealId,
        loanNumber: e.loanNumber,
        loanName: e.loanName,
        edits: [],
        views: 0,
      });
    }
    const bucket = byLoan.get(key);
    if (isViewCategory(e.category)) bucket.views += 1;
    else bucket.edits.push(e);
  });

  const loans = [...byLoan.values()].sort(
    (a, b) => b.edits.length - a.edits.length,
  );

  return (
    <Box
      sx={{
        pt: 0.25,
        pb: 0.5,
        // Same gutter as the session rows and the idle markers: one continuous
        // vertical, solid here to distinguish "inside a session" from the
        // dotted "nothing happened" gaps between them.
        ml: `${SEQ_RULE_X}px`,
        pl: `${SEQ_TEXT_PL}px`,
        borderLeft: `${SEQ_RULE_W}px solid ${slate[200]}`,
      }}
    >
      {loans.map((l) => (
        <Box key={l.dealId || l.loanNumber || "unknown"} sx={{ mt: 1 }}>
          <Flx ac g={1} wrap sx={{ mb: 0.25 }}>
            <SessionLoans session={{ loanList: [l] }} />
            {l.loanName ? (
              <Txt sx={{ fontSize: 11.5, color: S.muted }}>{l.loanName}</Txt>
            ) : null}
            <Txt sx={{ fontSize: 11, color: S.sub }}>
              {l.edits.length} edit{l.edits.length === 1 ? "" : "s"}
              {l.views ? ` · ${l.views} view${l.views === 1 ? "" : "s"}` : ""}
            </Txt>
          </Flx>

          {l.edits.length === 0 ? (
            <Txt sx={{ fontSize: 11.5, color: S.faint }}>
              Viewed only — nothing changed.
            </Txt>
          ) : (
            l.edits.map((e, i) => <EventDetail key={`${e.ts}-${i}`} event={e} />)
          )}
        </Box>
      ))}
    </Box>
  );
};

SessionDetail.propTypes = { session: PropTypes.object };

/** One edit: when, what kind, and the fields it moved. */
const EventDetail = ({ event }) => {
  const fields = detailFields({ changed_fields: event.changedFields });
  const shown = fields.slice(0, MAX_FIELDS);
  const hidden = fields.length - shown.length;

  return (
    <Box sx={{ py: 0.375 }}>
      <Flx ac g={1} wrap>
        <Txt
          sx={{
            fontSize: 11.5,
            fontVariantNumeric: "tabular-nums",
            color: S.sub,
            minWidth: 62,
          }}
        >
          {formatHour(event.hour)}
        </Txt>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: slate[800] }}>
          {actionPhrase({
            event_type: event.eventType,
            sub_event: event.subEvent,
          })}
        </Txt>
        {event.statusChange ? (
          <Txt sx={{ fontSize: 11.5, color: S.muted }}>
            → {String(event.statusChange)}
          </Txt>
        ) : null}
      </Flx>

      {shown.length ? (
        <Box sx={{ pl: "70px", pt: 0.25 }}>
          {shown.map(([key, raw]) => {
            const c = fieldChange(raw);
            return (
              <Flx key={key} g={0.75} wrap sx={{ alignItems: "baseline" }}>
                <Txt sx={{ fontSize: 11.5, color: S.sub, minWidth: 118 }}>
                  {FIELD_LABELS[key] || key}
                </Txt>
                {c.hasPrior ? (
                  <Txt sx={{ fontSize: 11.5, color: slate[800] }}>
                    <Box
                      component="span"
                      sx={{ color: S.sub, textDecoration: "line-through" }}
                    >
                      {fmtVal(c.from)}
                    </Box>{" "}
                    → <b>{fmtVal(c.to)}</b>
                  </Txt>
                ) : (
                  // No prior recorded for this event — say "set to" rather than
                  // draw an arrow from a blank, which would read as "was empty".
                  <Txt sx={{ fontSize: 11.5, color: slate[800] }}>
                    set to <b>{fmtVal(c.to)}</b>
                  </Txt>
                )}
              </Flx>
            );
          })}
          {hidden > 0 ? (
            <Txt sx={{ fontSize: 11, color: S.faint }}>
              +{hidden} more field{hidden === 1 ? "" : "s"}
            </Txt>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

EventDetail.propTypes = { event: PropTypes.object };

const SessionTooltip = ({ session }) => (
  <Box sx={{ py: 0.25, fontSize: 12, lineHeight: 1.7, whiteSpace: "nowrap" }}>
    <Flx ac jb g={2.25}>
      <span>
        {formatHour(session.start)} – {formatHour(session.end)}
      </span>
      <b>{formatDuration(session.end - session.start)}</b>
    </Flx>
    {[
      ["Edits", session.edits],
      ["Views", session.views],
      ["Loans", session.loans],
    ].map(([label, v]) => (
      <Flx key={label} ac jb g={2.25}>
        <span>{label}</span>
        <b style={{ fontVariantNumeric: "tabular-nums" }}>{fmtInt(v)}</b>
      </Flx>
    ))}
  </Box>
);

SessionTooltip.propTypes = { session: PropTypes.object };

const TimelineAxis = ({ axis }) => (
  <Flx
    ac
    g={2}
    sx={{ px: 2.25, pt: 1.25, pb: 0.25, borderBottom: `1px solid ${S.border}` }}
  >
    <Box sx={{ width: NAME_W, flexShrink: 0 }} />
    <Box sx={{ flex: 1, position: "relative", height: 14, minWidth: 300 }}>
      {axis.ticks.map((h) => (
        <Txt
          key={h}
          sx={{
            position: "absolute",
            top: 0,
            left: `${pctIn(axis, h)}%`,
            transform: "translateX(-50%)",
            fontSize: 10,
            color: S.faint,
          }}
        >
          {tickLabel(h)}
        </Txt>
      ))}
    </Box>
    <Box sx={{ width: TOTALS_W, flexShrink: 0 }} />
  </Flx>
);

TimelineAxis.propTypes = { axis: PropTypes.object };

/** The strip's contents for one person, in whatever state their fetch is in. */
const Strip = ({ axis, result }) => {
  if (result.status === "loading") {
    return (
      <Flx ac g={1} sx={{ height: "100%" }}>
        <CircularProgress size={12} thickness={6} />
        <Txt sx={{ fontSize: 11.5, color: S.sub }}>Reading their loans…</Txt>
      </Flx>
    );
  }
  if (result.status === "error") {
    return (
      <Flx ac sx={{ height: "100%" }}>
        <Txt sx={{ fontSize: 11.5, color: S.sub }}>
          Couldn&apos;t read this day&apos;s events.
        </Txt>
      </Flx>
    );
  }
  if (result.status !== "ready") {
    return (
      <Flx ac sx={{ height: "100%" }}>
        <Txt sx={{ fontSize: 11.5, color: S.faint }}>
          Open to load their sessions
        </Txt>
      </Flx>
    );
  }
  if (!result.sessions.length) {
    return (
      <Flx ac sx={{ height: "100%" }}>
        <Txt sx={{ fontSize: 11.5, color: S.faint }}>
          No timestamped events found on this day
        </Txt>
      </Flx>
    );
  }

  return (
    <>
      {axis.ticks.map((h) => (
        <Box
          key={h}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${pctIn(axis, h)}%`,
            width: "1px",
            bgcolor: "#F0F3F6",
          }}
        />
      ))}
      {result.sessions.map((s, i) => (
        <Tooltip
          key={`${s.start}-${i}`}
          arrow
          placement="top"
          title={<SessionTooltip session={s} />}
          componentsProps={{
            tooltip: { sx: { bgcolor: slate[800], maxWidth: "none", px: 1.5 } },
            arrow: { sx: { color: slate[800] } },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 5,
              bottom: 5,
              left: `${pctIn(axis, s.start)}%`,
              width: `${Math.max(0.5, pctIn(axis, s.end) - pctIn(axis, s.start))}%`,
              minWidth: "3px",
              borderRadius: "4px",
              bgcolor: blockColor(s),
            }}
          />
        </Tooltip>
      ))}
    </>
  );
};

Strip.propTypes = { axis: PropTypes.object, result: PropTypes.object };

/**
 * Why a day may be drawn from less than all of it. Three separate reasons, and
 * the note names whichever apply rather than showing a bare "partial" badge.
 */
const PartialNote = ({ result }) => {
  const reasons = [];
  if (result.loansSkipped > 0) {
    reasons.push(
      `read ${result.loansRead} of ${result.loansRead + result.loansSkipped} loans (capped)`,
    );
  }
  if (result.unresolvedLoans > 0) {
    reasons.push(
      `${result.unresolvedLoans} loan${result.unresolvedLoans === 1 ? "" : "s"} not found in the pipeline`,
    );
  }
  return (
    <Txt
      sx={{ display: "block", fontSize: 11, color: S.sub, pt: 1, lineHeight: 1.6 }}
    >
      Sample: {fmtInt(result.located)} events placed on the clock
      {reasons.length ? ` — ${reasons.join(", ")}` : ""}. Anything logged against
      a loan the rollup recorded no number for can&apos;t be fetched at all.
    </Txt>
  );
};

PartialNote.propTypes = { result: PropTypes.object };

/** One person's day: the strip, and (when open) the session sequence under it. */
const TimelineRow = ({ row, axis, result, open, onToggle }) => {
  // Which sessions are opened into their per-loan detail. Kept here, not in the
  // panel's shared state: it's a per-person, per-visit reading aid.
  const [openSessions, setOpenSessions] = useState(() => new Set());
  const toggleSession = (key) =>
    setOpenSessions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sessions = result.status === "ready" ? result.sessions : [];
  const active = sessions.reduce((a, s) => a + (s.end - s.start), 0);
  // Latest first — the sequence reads the way you'd ask the question ("what did
  // they do last?"), and each gap is measured back to the session before it.
  const sequence = [...sessions].sort((a, b) => b.start - a.start);
  // The reconstruction is a capped join on `loans_touched`, so it is often a
  // SAMPLE of the day. Say so plainly — a partial strip presented as complete
  // is worse than no strip.
  const partial =
    result.status === "ready" &&
    (result.loansSkipped > 0 || result.unresolvedLoans > 0);

  return (
    <Box>
      <Flx
        ac
        g={2}
        onClick={onToggle}
        sx={{
          px: 2.25,
          py: 1.125,
          cursor: "pointer",
          borderBottom: `1px solid ${S.border}`,
          bgcolor: isSystem(row) ? S.sysRow : "transparent",
          transition: "background .12s",
          "&:hover": { bgcolor: S.hover },
        }}
      >
        <Box sx={{ width: NAME_W, flexShrink: 0 }}>
          <UserIdentity row={row} />
        </Box>

        <Box sx={{ flex: 1, position: "relative", height: 28, minWidth: 300 }}>
          <Strip axis={axis} result={result} />
        </Box>

        <Flx
          column
          sx={{
            width: TOTALS_W,
            flexShrink: 0,
            alignItems: "flex-end",
            lineHeight: 1.3,
          }}
        >
          {result.status === "ready" ? (
            <Txt sx={{ fontSize: 12.5, color: S.muted }}>
              <b style={{ color: S.accent }}>{fmtInt(result.edits)}</b> edits ·{" "}
              {fmtInt(result.views)} views
            </Txt>
          ) : (
            // Before loading, the only honest number is an upper bound: the
            // rollup rows behind this entry are UTC days, and this row is an
            // Eastern one, so their totals include work from either side of it.
            <Txt sx={{ fontSize: 12.5, color: S.sub }}>
              up to {fmtInt(row.editsUpper)} edits
            </Txt>
          )}
          {sessions.length ? (
            <Txt sx={{ fontSize: 10.5, color: S.sub }}>
              {formatDuration(active)} active · {sessions.length} session
              {sessions.length === 1 ? "" : "s"}
              {partial ? " · sample" : ""}
            </Txt>
          ) : (
            <Txt sx={{ fontSize: 10.5, color: S.sub }}>
              {fmtInt(row.loansUpper)} loan
              {num(row.loansUpper) === 1 ? "" : "s"} to read
            </Txt>
          )}
        </Flx>
      </Flx>

      {open && result.status === "ready" ? (
        <Box
          sx={{
            pl: `${NAME_W + 34}px`,
            pr: 2.25,
            py: 1,
            bgcolor: S.detailBg,
            borderBottom: `1px solid ${S.border}`,
          }}
        >
          {sequence.length === 0 ? (
            <Txt sx={{ fontSize: 12.5, color: S.sub }}>
              None of this person&apos;s loans returned a timestamped event for
              this day.
            </Txt>
          ) : (
            sequence.map((s, i) => {
              const earlier = sequence[i + 1];
              const key = `${s.start}-${i}`;
              const isOpen = openSessions.has(key);
              return (
                <Box key={`${s.start}-seq-${i}`}>
                  <Flx
                    ac
                    wrap
                    onClick={() => toggleSession(key)}
                    sx={{
                      py: 0.75,
                      cursor: "pointer",
                      borderRadius: "6px",
                      "&:hover": { bgcolor: "#F4F7F9" },
                    }}
                  >
                    {/* The gutter itself — a fixed box, NOT a gap, so the text
                        after it lands on SEQ_GUTTER exactly. */}
                    <Flx center sx={{ width: SEQ_GUTTER, flexShrink: 0 }}>
                      <ChevronRightRounded
                        sx={{
                          fontSize: 15,
                          color: S.faint,
                          transition: "transform .18s",
                          transform: isOpen ? "rotate(90deg)" : "none",
                        }}
                      />
                    </Flx>
                    <Txt
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatHour(s.start)} – {formatHour(s.end)}
                    </Txt>
                    <Flx ac g={1.5} wrap sx={{ ml: 1.5 }}>
                    <Box
                      sx={{
                        px: 1,
                        borderRadius: "999px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#fff",
                        bgcolor: blockColor(s),
                      }}
                    >
                      {formatDuration(s.end - s.start)}
                    </Box>
                    <Tooltip
                      arrow
                      placement="top"
                      title={<EditMixTooltip session={s} />}
                      componentsProps={{
                        tooltip: {
                          sx: { bgcolor: slate[800], maxWidth: "none", px: 1.5 },
                        },
                        arrow: { sx: { color: slate[800] } },
                      }}
                    >
                      <Box component="span" sx={{ cursor: "help" }}>
                        <Txt sx={{ fontSize: 12.5, color: S.muted }}>
                          <b
                            style={{
                              color: S.accent,
                              borderBottom: `1px dotted ${teal[300]}`,
                            }}
                          >
                            {fmtInt(s.edits)}
                          </b>{" "}
                          edits · {fmtInt(s.views)} views
                        </Txt>
                      </Box>
                    </Tooltip>
                    <SessionLoans session={s} />
                    </Flx>
                  </Flx>
                  {isOpen ? <SessionDetail session={s} /> : null}
                  {earlier ? (
                    <Txt
                      sx={{
                        display: "block",
                        fontSize: 11.5,
                        color: S.faint,
                        py: 0.375,
                        ml: `${SEQ_RULE_X}px`,
                        pl: `${SEQ_TEXT_PL}px`,
                        borderLeft: `${SEQ_RULE_W}px dotted ${slate[300]}`,
                      }}
                    >
                      {formatDuration(s.start - earlier.end)} idle
                    </Txt>
                  ) : null}
                </Box>
              );
            })
          )}

          {partial ? <PartialNote result={result} /> : null}
        </Box>
      ) : null}
    </Box>
  );
};

TimelineRow.propTypes = {
  row: PropTypes.object,
  axis: PropTypes.object,
  result: PropTypes.object,
  open: PropTypes.bool,
  onToggle: PropTypes.func,
};

/**
 * The Timeline panel. `rows` are the filtered user×day entries; the day picker
 * scopes them to one calendar day, which is the only scale an intra-day axis
 * makes sense at.
 */
const UserActivityTimeline = ({
  rows,
  day,
  onDayChange,
  expanded,
  onToggleUser,
  sessions,
}) => {
  const { load, loadMany, resultFor, requestCost, pipelineReady } = sessions;

  // The chips are EASTERN days. The rollup's dates are UTC buckets, but for
  // daytime work the two coincide, so the UTC date set is the right chip set —
  // what changes is how each chip is FILLED.
  const days = [...new Set(rows.map((r) => r.date))].sort().reverse();
  const active = days.includes(day) ? day : days[0];

  /**
   * One entry per person on the selected EASTERN day.
   *
   * Sourced from TWO UTC rows — D and D+1 — because the backend files anything
   * after 8 PM Eastern under the next UTC day. Reading only row D is what put
   * last night's 9 PM session on today's strip.
   *
   * The totals carried here are upper bounds only (a UTC row spans parts of two
   * Eastern days); the real numbers come back with the sessions.
   */
  const dayRows = (() => {
    if (!active) return [];
    const byUser = new Map();
    const collect = (date) => {
      rows
        .filter((r) => r.date === date)
        .forEach((r) => {
          let e = byUser.get(r.user);
          if (!e) {
            e = {
              user: r.user,
              userName: r.userName,
              date: active,
              sourceRows: [],
              editsUpper: 0,
              loansUpper: 0,
            };
            byUser.set(r.user, e);
          }
          e.sourceRows.push(r);
          e.editsUpper += num(r.edits);
          e.loansUpper += num(r.loans);
        });
    };
    collect(active);
    collect(nextDayIso(active));
    return [...byUser.values()].sort((x, y) => y.editsUpper - x.editsUpper);
  })();

  // NOTHING loads on its own. Each person's day costs one request per loan they
  // touched, so the traffic is always something you asked for — a row when you
  // open it, or the whole day from the button, which says up front what it will
  // cost. (The single backend route in USER_ACTIVITY.md §6.1 removes all of it.)
  const results = dayRows.map((r) => resultFor(r));
  const loadedSpans = results
    .filter((x) => x.status === "ready")
    .flatMap((x) => x.sessions);
  const axis = axisFor(loadedSpans);
  const pending = dayRows.filter(
    (r, i) => results[i].status === "idle" || results[i].status === undefined,
  );
  const cost = pipelineReady ? requestCost(pending) : 0;

  return (
    <Box>
      <Flx
        ac
        g={1}
        sx={{
          px: 2.25,
          py: 1.5,
          borderBottom: `1px solid ${S.border}`,
          overflowX: "auto",
        }}
      >
        {days.length === 0 ? (
          <Txt sx={{ fontSize: 12.5, color: S.sub }}>No days in this window.</Txt>
        ) : (
          days.map((d) => {
            const on = d === active;
            const chip = formatDayChip(d);
            return (
              <Box
                key={d}
                component="button"
                type="button"
                onClick={() => onDayChange(d)}
                sx={{
                  font: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: 1.625,
                  py: 0.625,
                  cursor: "pointer",
                  borderRadius: "999px",
                  border: `1px solid ${on ? S.accent : S.border}`,
                  bgcolor: on ? S.accent : S.surface,
                  color: on ? "#fff" : S.muted,
                  transition: "all .15s",
                  "&:hover": on
                    ? { bgcolor: teal[700], borderColor: teal[700] }
                    : { color: S.accent, borderColor: S.accent },
                }}
              >
                {chip.label}
                {chip.date ? (
                  // Dimmed rather than recoloured: the chip is white when idle
                  // and teal when selected, and opacity reads correctly on both.
                  <Box component="span" sx={{ opacity: 0.65, ml: 0.5 }}>
                    · {chip.date}
                  </Box>
                ) : null}
              </Box>
            );
          })
        )}
      </Flx>

      {dayRows.length === 0 ? (
        <Txt
          sx={{
            display: "block",
            textAlign: "center",
            fontSize: 13,
            color: S.label,
            py: 5,
          }}
        >
          No activity recorded on this day.
        </Txt>
      ) : (
        <Box>
          <TimelineAxis axis={axis} />
          {dayRows.map((r, i) => (
            <TimelineRow
              key={r.user}
              row={r}
              axis={axis}
              result={results[i]}
              open={expanded.has(r.user)}
              onToggle={() => {
                load(r);
                onToggleUser(r.user);
              }}
            />
          ))}
          <Flx
            ac
            jb
            g={1}
            wrap
            sx={{ px: 2.25, py: 1.25, bgcolor: S.detailBg }}
          >
            <Txt sx={{ fontSize: 11.5, color: S.sub, lineHeight: 1.6 }}>
              Eastern days, and Eastern clock. The rollup buckets by UTC date,
              so each day here is assembled from two of its rows — otherwise
              anything after 8 PM lands on the next day. Read from each
              loan&apos;s audit trail, up to {MAX_DEALS_PER_ROW} loans per
              person, split wherever they went quiet for more than{" "}
              {IDLE_GAP_MIN} minutes.
            </Txt>
            {pending.length ? (
              <Button
                size="small"
                variant="outlined"
                disabled={!pipelineReady}
                onClick={() => loadMany(pending)}
                sx={{ fontSize: 12, textTransform: "none", flexShrink: 0 }}
              >
                {`Load the other ${pending.length}`}
                {cost ? ` · ${cost} request${cost === 1 ? "" : "s"}` : ""}
              </Button>
            ) : null}
          </Flx>
        </Box>
      )}
    </Box>
  );
};

UserActivityTimeline.propTypes = {
  rows: PropTypes.array,
  day: PropTypes.string,
  onDayChange: PropTypes.func,
  expanded: PropTypes.instanceOf(Set),
  onToggleUser: PropTypes.func,
  sessions: PropTypes.object,
};

export default UserActivityTimeline;
